import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DigitalIdService } from '../../service/digital-id.service';
import { UntilDestroy } from '@ngneat/until-destroy';
import { setupUntilDestroy } from '@portal-workspace/grow-ui-library';
import { Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { MinimalLayoutService } from '../../layout/minimal-layout/minimal-layout.service';
import { PortalHotToastService, ApplicationDialogService } from '@portal-workspace/grow-ui-library';
import { CommonModule } from '@angular/common';
import { MessageBoxComponent } from '@portal-workspace/grow-ui-library';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ApplicationVerificationIndividual, ApplicationVerificationPayloadResult, KycStatus } from '@portal-workspace/grow-shared-library';

@UntilDestroy({ arrayName: 'subscriptions' })
@Component({
    templateUrl: './application-verification.page.html',
    styleUrls: ['./application-verification.page.scss'],
    standalone: true,
    imports: [
        CommonModule,
        MessageBoxComponent,
        MatButtonModule,
        MatProgressSpinnerModule,
    ],
})
export class ApplicationVerificationPage implements OnInit {
    subscriptions: Subscription[] = [];

    applicationId: number | null = null;
    applicationData?: ApplicationVerificationPayloadResult;
    alerts: string[] = [];
    messages: string[] = [];
    isLoading = false;
    sendingIndividualId: string | null = null;

    constructor(
        private digitalIdService: DigitalIdService,
        private minimalLayoutService: MinimalLayoutService,
        private portalHotToastService: PortalHotToastService,
        private applicationDialogService: ApplicationDialogService,
        private route: ActivatedRoute
    ) { }

    ngOnInit(): void {
        setupUntilDestroy(this);
        this.minimalLayoutService.settings({
            showPrev: false,
            showCross: false,
            showContact: true,
            showAccreditationInContact: false,
        });

        const appIdParam = this.route.snapshot.params.applicationId;
        let appId: string;

        // Check if it's a plain number (backward compatibility)
        if (!isNaN(Number(appIdParam))) {
            appId = appIdParam;
        } else {
            // Assume it's base64 encoded
            try {
                appId = atob(appIdParam); // Decode from base64
            } catch (e) {
                this.alerts.push('Invalid application ID');
                return;
            }
        }

        if (!appId || isNaN(Number(appId))) {
            this.alerts.push('Invalid application ID');
            return;
        }

        this.applicationId = Number(appId);
        this.loadApplicationVerification();
    }

    loadApplicationVerification(): void {
        if (!this.applicationId) return;

        this.isLoading = true;
        this.subscriptions.push(
            this.digitalIdService.checkApplicationVerification(this.applicationId).pipe(
                tap((response) => {
                    this.isLoading = false;
                    if (!response.status) {
                        this.alerts.push(response.message || 'Failed to load verification data');
                    } else {
                        this.applicationData = response.payload;
                    }
                })
            ).subscribe()
        );
    }

    sendVerificationSms(individual: ApplicationVerificationIndividual): void {
        if (!this.applicationId) return;

        this.sendingIndividualId = individual.individualId;
        this.subscriptions.push(
            this.digitalIdService
                .sendVerificationSms(this.applicationId, individual.individualId)
                .pipe(
                    this.portalHotToastService.spinnerObservable(),
                    tap(
                        (response) => {
                            this.sendingIndividualId = null;
                            if (response && response.status) {
                                const recipientName = `${individual.individual.givenName ?? ''} ${individual.individual.surname ?? ''}`;
                                // Show success dialog and also let them know that the link will expire in 24 hours.
                                this.applicationDialogService.successDialog({
                                    message: 'SMS Sent Successfully',
                                    subMessage: `Verification SMS has been sent to ${recipientName}. They will receive an SMS with a link to complete their identity verification. The link will expire in 24 hours.`
                                }).afterClosed().subscribe(() => {
                                    // Reload to get updated status
                                    this.loadApplicationVerification();
                                });
                            }
                        },
                        (error) => {
                            this.sendingIndividualId = null;
                            this.applicationDialogService.openAlertDialog({
                                message: 'Error',
                                subMessage: error.message || 'Failed to send verification SMS'
                            });
                        }
                    )
                )
                .subscribe()
        );
    }

    getRecipientName(individual: ApplicationVerificationIndividual): string {
        const ind = individual.individual;
        if (!ind) return '';
        return `${ind.givenName ?? ''} ${ind.middleName ? ind.middleName + ' ' : ''}${ind.surname ?? ''}`;
    }

    getMaskedMobile(individual: ApplicationVerificationIndividual): string {
        const mobile = individual.individual?.mobileNumber || '';
        if (mobile.length < 6) return mobile;
        return mobile.substring(0, 2) + '****' + mobile.substring(mobile.length - 4);
    }

    getVerificationStatusDisplay(status: KycStatus): string {
        switch (status) {
            case 'RECEIVED_COMPLETE':
                return 'Verified';
            case 'SMS_SENT':
                return 'Pending';
            case 'WAITING':
                return 'Not Started';
            case 'RECEIVED_INCOMPLETE':
                return 'Incomplete';
            case 'RECEIVED_PENDING_REVIEW':
                return 'Under Review';
            case 'RECEIVED_FAILED':
                return 'Failed';
            case 'RECEIVED_ERROR':
                return 'Failed';
            default:
                return 'Unknown';
        }
    }

    getStatusState(status: KycStatus): string {
        switch (status) {
            case 'RECEIVED_COMPLETE':
                return 'success';
            case 'SMS_SENT':
                return 'pending';
            case 'WAITING':
                return 'info';
            case 'RECEIVED_INCOMPLETE':
                return 'warning';
            case 'RECEIVED_PENDING_REVIEW':
                return 'warning';
            case 'RECEIVED_FAILED':
                return 'danger';
            case 'RECEIVED_ERROR':
                return 'danger';
            default:
                return 'info';
        }
    }

    canSendSms(individual: ApplicationVerificationIndividual): boolean {
        return individual.status !== 'RECEIVED_COMPLETE';
    }

    refresh(): void {
        this.alerts = [];
        this.messages = [];
        this.loadApplicationVerification();
    }

    // Helper to get company name if available
    getCompanyName(): string | null {
        return this.applicationData?.companyName || null;
    }
}
